import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Lock, Eye, EyeOff, Loader2, Plus, Printer, Trash2, KeyRound, IndianRupee, Receipt, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { API, api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const GST_RATES = [0, 5, 12, 18];
const fapi = (t) => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${t}` } });

function PwInput({ testId, value, onChange, placeholder }) {
    const [show, setShow] = useState(false);
    return (
        <div className="relative">
            <Input data-testid={testId} type={show ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder} className="pr-10" />
            <button type="button" data-testid={`${testId}-eye`} onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
        </div>
    );
}

export default function Finance() {
    const [configured, setConfigured] = useState(null);
    const [token, setToken] = useState(localStorage.getItem("finance_token") || "");
    const [view, setView] = useState("unlock"); // unlock | forgot
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [otp, setOtp] = useState("");
    const [newPw, setNewPw] = useState("");
    const [busy, setBusy] = useState(false);
    const [summary, setSummary] = useState(null);
    const [invoices, setInvoices] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [viewInv, setViewInv] = useState(null);
    const [patientQ, setPatientQ] = useState("");
    const [patientResults, setPatientResults] = useState([]);
    const [selected, setSelected] = useState(null);
    const [inv, setInv] = useState({ date: new Date().toISOString().slice(0, 10), items: [{ description: "", qty: 1, rate: 0, gst_rate: 0 }], payment_mode: "Cash", insurance_company: "", notes: "" });
    const [saving, setSaving] = useState(false);
    const [params] = useSearchParams();

    useEffect(() => {
        if (params.get("new") === "1") {
            setShowNew(true);
            if (params.get("pid")) setSelected({ id: params.get("pid"), name: params.get("pname") || "", patient_uid: "" });
            if (params.get("desc")) setInv((prev) => ({ ...prev, items: [{ description: params.get("desc"), qty: 1, rate: 0, gst_rate: 0 }] }));
        }
    }, []);

    useEffect(() => {
        api.get("/finance/status").then(({ data }) => setConfigured(data.configured)).catch(() => setConfigured(false));
    }, []);

    const load = async (t = token) => {
        try {
            const [{ data: s }, { data: invs }] = await Promise.all([
                fapi(t).get("/finance/summary"),
                fapi(t).get("/finance/invoices"),
            ]);
            setSummary(s);
            setInvoices(invs);
        } catch {
            localStorage.removeItem("finance_token");
            setToken("");
        }
    };

    useEffect(() => { if (token) load(); }, [token]);

    useEffect(() => {
        if (!patientQ.trim() || selected) { setPatientResults([]); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/patients", { params: { search: patientQ } });
                setPatientResults(data);
            } catch { setPatientResults([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [patientQ, selected]);

    const unlock = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/finance/unlock", { password });
            localStorage.setItem("finance_token", data.token);
            setToken(data.token);
            setPassword("");
            toast.success("Finance unlocked");
        } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
    };

    const setupPw = async (e) => {
        e.preventDefault();
        if (password !== confirm) { toast.error("Passwords do not match"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/finance/set", { password });
            localStorage.setItem("finance_token", data.token);
            setToken(data.token);
            setConfigured(true);
            toast.success("Finance password set");
        } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
    };

    const sendOtp = async () => {
        setBusy(true);
        try {
            await api.post("/finance/forgot");
            toast.success("OTP sent to your registered email");
        } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
    };

    const resetPw = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/finance/reset", { otp, new_password: newPw });
            localStorage.setItem("finance_token", data.token);
            setToken(data.token);
            setView("unlock");
            toast.success("Finance password reset");
        } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
    };

    const relock = () => {
        localStorage.removeItem("finance_token");
        setToken("");
    };

    const setItem = (i, k, v) => setInv({ ...inv, items: inv.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) });

    const totals = inv.items.reduce((acc, it) => {
        const taxable = (Number(it.qty) || 0) * (Number(it.rate) || 0);
        acc.sub += taxable;
        acc.gst += taxable * (Number(it.gst_rate) || 0) / 100;
        return acc;
    }, { sub: 0, gst: 0 });

    const createInvoice = async (e) => {
        e.preventDefault();
        if (!selected) { toast.error("Select a patient"); return; }
        if (!inv.items.some((it) => it.description.trim())) { toast.error("Add at least one item"); return; }
        setSaving(true);
        try {
            const payload = { ...inv, patient_id: selected.id, items: inv.items.filter((it) => it.description.trim()).map((it) => ({ ...it, qty: Number(it.qty) || 1, rate: Number(it.rate) || 0, gst_rate: Number(it.gst_rate) || 0 })) };
            const { data } = await fapi(token).post("/finance/invoices", payload);
            toast.success(`Invoice ${data.invoice_no} created`);
            setShowNew(false);
            setSelected(null);
            setPatientQ("");
            setInv({ date: new Date().toISOString().slice(0, 10), items: [{ description: "", qty: 1, rate: 0, gst_rate: 0 }], payment_mode: "Cash", insurance_company: "", notes: "" });
            load();
            openInvoice(data.id);
        } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
    };

    const openInvoice = async (invId) => {
        try {
            const { data } = await fapi(token).get(`/finance/invoices/${invId}`);
            setViewInv(data);
        } catch (err) { toast.error(fmtErr(err)); }
    };

    const deleteInvoice = async (invId) => {
        if (!window.confirm("Delete this invoice?")) return;
        try {
            await fapi(token).delete(`/finance/invoices/${invId}`);
            toast.success("Invoice deleted");
            load();
        } catch (err) { toast.error(fmtErr(err)); }
    };

    if (configured === null) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#E06D1F]" /></div>;

    if (!token) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center animate-fade-up" data-testid="finance-page">
                <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm" data-testid="finance-lock-card">
                    <div className="flex items-center gap-3 mb-5">
                        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FFF4EC] text-[#E06D1F]"><Lock className="h-5 w-5" /></span>
                        <div>
                            <h2 className="font-heading text-xl font-bold text-slate-900">Finance Access</h2>
                            <p className="text-xs text-slate-500">{configured ? "Enter your finance password" : "Set a finance password to continue"}</p>
                        </div>
                    </div>
                    {!configured ? (
                        <form onSubmit={setupPw} className="space-y-4">
                            <div className="space-y-1.5"><Label>Finance Password (PIN or word+number)</Label><PwInput testId="finance-setup-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 4 characters" /></div>
                            <div className="space-y-1.5"><Label>Confirm Password</Label><PwInput testId="finance-setup-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat" /></div>
                            <Button data-testid="finance-setup-btn" type="submit" disabled={busy} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Set Password & Enter</Button>
                        </form>
                    ) : view === "unlock" ? (
                        <form onSubmit={unlock} className="space-y-4">
                            <div className="space-y-1.5"><Label>Finance Password</Label><PwInput testId="finance-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Finance password" /></div>
                            <Button data-testid="finance-unlock-btn" type="submit" disabled={busy} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />} Unlock</Button>
                            <button type="button" data-testid="finance-forgot-link" onClick={() => setView("forgot")} className="text-sm font-medium text-[#C2410C] hover:underline">Forgot password?</button>
                        </form>
                    ) : (
                        <form onSubmit={resetPw} className="space-y-4" data-testid="finance-forgot-form">
                            <p className="text-sm text-slate-500">We'll send a 6-digit OTP to your registered email.</p>
                            <Button type="button" data-testid="finance-send-otp-btn" variant="outline" onClick={sendOtp} disabled={busy} className="w-full">Send OTP to my email</Button>
                            <div className="space-y-1.5"><Label>OTP</Label><Input data-testid="finance-otp-input" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit OTP" maxLength={6} /></div>
                            <div className="space-y-1.5"><Label>New Finance Password</Label><PwInput testId="finance-new-password-input" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Min 4 characters" /></div>
                            <Button data-testid="finance-reset-btn" type="submit" disabled={busy} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Reset & Enter</Button>
                            <button type="button" onClick={() => setView("unlock")} className="text-sm text-slate-500 hover:underline">Back to unlock</button>
                        </form>
                    )}
                </div>
            </div>
        );
    }

    const cards = [
        { label: "Total Revenue", value: summary ? `₹${summary.total_revenue.toLocaleString("en-IN")}` : "—", icon: IndianRupee, testId: "fin-stat-revenue" },
        { label: "This Month", value: summary ? `₹${summary.month_revenue.toLocaleString("en-IN")}` : "—", icon: CalendarClock, testId: "fin-stat-month" },
        { label: "GST Collected", value: summary ? `₹${summary.gst_collected.toLocaleString("en-IN")}` : "—", icon: Receipt, testId: "fin-stat-gst" },
        { label: "Invoices", value: summary ? summary.invoice_count : "—", icon: Receipt, testId: "fin-stat-count" },
    ];

    return (
        <div className="space-y-5 animate-fade-up" data-testid="finance-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Finance</h1>
                    <p className="mt-1 text-base text-slate-500">Invoices with GST — Cash / Medical Policy</p>
                </div>
                <div className="flex gap-2">
                    <Button data-testid="finance-lock-btn" variant="outline" onClick={relock} className="gap-1.5"><Lock className="h-4 w-4" /> Lock</Button>
                    <Button data-testid="new-invoice-btn" onClick={() => setShowNew(true)} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-2"><Plus className="h-4 w-4" /> New Invoice</Button>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {cards.map((c) => (
                    <div key={c.label} data-testid={c.testId} className="rounded-xl border border-slate-200 bg-white p-5 flex items-center gap-4 shadow-sm">
                        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FFF4EC] text-[#E06D1F]"><c.icon className="h-5 w-5" /></span>
                        <div><p className="font-heading text-2xl font-bold text-slate-900">{c.value}</p><p className="text-xs text-slate-500">{c.label}</p></div>
                    </div>
                ))}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="invoices-card">
                {!invoices ? (
                    <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>
                ) : invoices.length === 0 ? (
                    <p className="px-5 py-12 text-center text-sm text-slate-500" data-testid="no-invoices">No invoices yet.</p>
                ) : (
                    <table className="w-full text-sm">
                        <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                            <th className="px-5 py-3">Invoice</th><th className="px-5 py-3">Patient</th><th className="px-5 py-3 hidden sm:table-cell">Date</th><th className="px-5 py-3 hidden md:table-cell">Mode</th><th className="px-5 py-3 text-right">Total</th><th className="px-5 py-3"></th>
                        </tr></thead>
                        <tbody>
                            {invoices.map((i) => (
                                <tr key={i.id} data-testid={`invoice-row-${i.invoice_no}`} className="border-t border-slate-100 hover:bg-[#FFF4EC]/60 transition-colors duration-100">
                                    <td className="px-5 py-3 font-mono2 text-xs font-semibold text-[#C2410C]">{i.invoice_no}</td>
                                    <td className="px-5 py-3 font-medium text-slate-900">{i.patient_name} <span className="text-xs text-slate-400 font-mono2">{i.patient_uid}</span></td>
                                    <td className="px-5 py-3 hidden sm:table-cell text-slate-600">{i.date}</td>
                                    <td className="px-5 py-3 hidden md:table-cell"><span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${i.payment_mode === "Medical Policy" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{i.payment_mode}</span>{i.insurance_company ? <span className="ml-1 text-xs text-slate-400">{i.insurance_company}</span> : ""}</td>
                                    <td className="px-5 py-3 text-right font-semibold text-slate-900">₹{i.total.toLocaleString("en-IN")}</td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap">
                                        <Button data-testid={`invoice-view-${i.invoice_no}`} size="sm" variant="outline" onClick={() => openInvoice(i.id)}>View</Button>
                                        <Button data-testid={`invoice-delete-${i.invoice_no}`} size="sm" variant="ghost" onClick={() => deleteInvoice(i.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Dialog open={showNew} onOpenChange={setShowNew}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-invoice-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">New Invoice</DialogTitle>
                        <DialogDescription>GST is split as CGST/SGST on the invoice. Healthcare services are usually GST-exempt — keep 0% unless applicable.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={createInvoice} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5 relative">
                                <Label>Patient *</Label>
                                {selected ? (
                                    <div className="flex items-center justify-between rounded-lg border border-orange-200 bg-[#FFF4EC] px-3 py-2.5" data-testid="inv-selected-patient">
                                        <span className="text-sm font-medium text-slate-900">{selected.name} <span className="text-xs text-slate-500 font-mono2">{selected.patient_uid}</span></span>
                                        <button type="button" onClick={() => { setSelected(null); setPatientQ(""); }} className="text-xs text-slate-500 hover:text-red-600">Change</button>
                                    </div>
                                ) : (
                                    <>
                                        <Input data-testid="inv-patient-search-input" value={patientQ} onChange={(e) => setPatientQ(e.target.value)} placeholder="Search patient..." />
                                        {patientResults.length > 0 && (
                                            <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-48 overflow-y-auto" data-testid="inv-patient-results">
                                                {patientResults.map((p) => (
                                                    <button key={p.id} type="button" data-testid={`inv-patient-option-${p.patient_uid}`} onClick={() => { setSelected(p); setPatientResults([]); if (p.insurance_company) setInv((prev) => ({ ...prev, payment_mode: "Medical Policy", insurance_company: p.insurance_company })); }} className="w-full text-left px-4 py-2.5 hover:bg-[#FFF4EC] border-b border-slate-100 last:border-0">
                                                        <span className="text-sm font-medium text-slate-900">{p.name}</span><span className="ml-2 text-xs text-slate-500 font-mono2">{p.patient_uid}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="space-y-1.5"><Label>Date</Label><Input data-testid="inv-date-input" type="date" value={inv.date} onChange={(e) => setInv({ ...inv, date: e.target.value })} /></div>
                        </div>
                        <div className="space-y-2">
                            <Label>Items</Label>
                            {inv.items.map((it, i) => (
                                <div key={i} className="grid grid-cols-[1fr_64px_90px_76px_90px_32px] gap-2 items-center" data-testid={`inv-item-row-${i}`}>
                                    <Input data-testid={`inv-item-desc-${i}`} value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} placeholder="Consultation / Procedure..." />
                                    <Input data-testid={`inv-item-qty-${i}`} type="number" min="1" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
                                    <Input data-testid={`inv-item-rate-${i}`} type="number" min="0" value={it.rate} onChange={(e) => setItem(i, "rate", e.target.value)} placeholder="Rate ₹" />
                                    <select data-testid={`inv-item-gst-${i}`} value={it.gst_rate} onChange={(e) => setItem(i, "gst_rate", e.target.value)} className="rounded-lg border border-slate-300 bg-white py-2.5 px-2 text-xs outline-none focus:border-orange-500">
                                        {GST_RATES.map((g) => <option key={g} value={g}>{g}% GST</option>)}
                                    </select>
                                    <p className="text-xs font-semibold text-slate-900 text-right">₹{(((Number(it.qty) || 0) * (Number(it.rate) || 0)) * (1 + (Number(it.gst_rate) || 0) / 100)).toLocaleString("en-IN")}</p>
                                    <button type="button" data-testid={`inv-remove-item-${i}`} onClick={() => setInv({ ...inv, items: inv.items.filter((_, idx) => idx !== i) })} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                                </div>
                            ))}
                            <Button type="button" data-testid="inv-add-item-btn" variant="outline" size="sm" onClick={() => setInv({ ...inv, items: [...inv.items, { description: "", qty: 1, rate: 0, gst_rate: 0 }] })} className="gap-1.5"><Plus className="h-3.5 w-3.5" /> Add Item</Button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label>Payment Mode</Label>
                                <select data-testid="inv-payment-mode-select" value={inv.payment_mode} onChange={(e) => setInv({ ...inv, payment_mode: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none">
                                    <option value="Cash">Cash</option>
                                    <option value="Medical Policy">Medical Policy</option>
                                </select>
                            </div>
                            {inv.payment_mode === "Medical Policy" && (
                                <div className="space-y-1.5"><Label>Insurance Company</Label><Input data-testid="inv-insurance-input" value={inv.insurance_company} onChange={(e) => setInv({ ...inv, insurance_company: e.target.value })} placeholder="Star Health..." /></div>
                            )}
                        </div>
                        <div className="space-y-1.5"><Label>Notes</Label><Input data-testid="inv-notes-input" value={inv.notes} onChange={(e) => setInv({ ...inv, notes: e.target.value })} placeholder="Optional" /></div>
                        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm space-y-1" data-testid="inv-totals">
                            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><b>₹{totals.sub.toLocaleString("en-IN")}</b></div>
                            <div className="flex justify-between"><span className="text-slate-500">GST</span><b>₹{totals.gst.toLocaleString("en-IN")}</b></div>
                            <div className="flex justify-between text-base border-t border-slate-200 pt-1"><span className="font-semibold">Total</span><b className="text-[#C2410C]">₹{(totals.sub + totals.gst).toLocaleString("en-IN")}</b></div>
                        </div>
                        <Button data-testid="inv-submit-btn" type="submit" disabled={saving || !selected} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create Invoice</Button>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={!!viewInv} onOpenChange={() => setViewInv(null)}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-view-dialog">
                    {viewInv && (
                        <div>
                            <div className="flex items-start justify-between border-b-2 border-[#E06D1F] pb-4">
                                <div>
                                    <img src="/zen-logo.webp" alt="Zen Hospital" className="h-12 w-auto" />
                                    <p className="mt-1.5 text-xs text-slate-500 max-w-[280px]">{viewInv.doctor.clinic_address}</p>
                                    <p className="text-xs text-slate-500">Ph: {viewInv.doctor.clinic_phone} · {viewInv.doctor.clinic_email}</p>
                                </div>
                                <div className="text-right">
                                    <h3 className="font-heading text-lg font-bold text-slate-900">TAX INVOICE</h3>
                                    <p className="text-sm font-mono2 font-semibold text-[#C2410C]" data-testid="invoice-view-no">{viewInv.invoice.invoice_no}</p>
                                    <p className="text-xs text-slate-500">Date: {viewInv.invoice.date}</p>
                                </div>
                            </div>
                            <div className="py-3 text-sm">
                                <p><span className="text-slate-500">Bill To:</span> <b>{viewInv.patient.name}</b> <span className="font-mono2 text-xs text-slate-400">{viewInv.patient.patient_uid}</span></p>
                                <p className="text-xs text-slate-500">{viewInv.patient.phone}{viewInv.patient.address ? ` · ${viewInv.patient.address}` : ""}</p>
                            </div>
                            <table className="w-full text-sm border border-slate-200">
                                <thead><tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 text-left">
                                    <th className="px-3 py-2">Description</th><th className="px-3 py-2 text-center">Qty</th><th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">GST</th><th className="px-3 py-2 text-right">Amount</th>
                                </tr></thead>
                                <tbody>
                                    {viewInv.invoice.items.map((it, i) => (
                                        <tr key={i} className="border-t border-slate-100">
                                            <td className="px-3 py-2">{it.description}</td>
                                            <td className="px-3 py-2 text-center">{it.qty}</td>
                                            <td className="px-3 py-2 text-right">₹{it.rate.toLocaleString("en-IN")}</td>
                                            <td className="px-3 py-2 text-right">{it.gst_rate}%</td>
                                            <td className="px-3 py-2 text-right font-medium">₹{it.amount.toLocaleString("en-IN")}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="flex justify-end mt-3">
                                <div className="w-64 text-sm space-y-1" data-testid="invoice-view-totals">
                                    <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>₹{viewInv.invoice.subtotal.toLocaleString("en-IN")}</span></div>
                                    <div className="flex justify-between"><span className="text-slate-500">CGST</span><span>₹{(viewInv.invoice.gst_total / 2).toLocaleString("en-IN")}</span></div>
                                    <div className="flex justify-between"><span className="text-slate-500">SGST</span><span>₹{(viewInv.invoice.gst_total / 2).toLocaleString("en-IN")}</span></div>
                                    <div className="flex justify-between font-bold text-base border-t border-slate-300 pt-1"><span>Grand Total</span><span className="text-[#C2410C]">₹{viewInv.invoice.total.toLocaleString("en-IN")}</span></div>
                                    <div className="flex justify-between text-xs"><span className="text-slate-500">Payment</span><span>{viewInv.invoice.payment_mode}{viewInv.invoice.insurance_company ? ` (${viewInv.invoice.insurance_company})` : ""}</span></div>
                                </div>
                            </div>
                            {viewInv.invoice.notes && <p className="mt-3 text-xs text-slate-500">Notes: {viewInv.invoice.notes}</p>}
                            <div className="mt-6 flex justify-between items-end">
                                <p className="text-[10px] text-slate-400">This is a computer-generated invoice from Zen Hospital.</p>
                                <div className="text-right"><p className="text-xs text-slate-400 italic">(Authorised Signatory)</p><p className="text-sm font-semibold text-slate-900">{viewInv.doctor.name?.startsWith("Dr") ? viewInv.doctor.name : `Dr. ${viewInv.doctor.name}`}</p></div>
                            </div>
                            <div className="mt-5 flex justify-end no-print">
                                <Button data-testid="invoice-print-btn" onClick={() => window.print()} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-1.5"><Printer className="h-4 w-4" /> Print Invoice</Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
